import React, { useState } from 'react'
import {
  Box,
  Typography,
  Divider,
  Button,
  Dialog,
  CloseIcon,
  ExperienceSlider,
  Frame,
  Heading
} from 'components'
import { updateRoles, deleteRole, useHistory, useContent } from 'utils'
import { SkillPill } from 'components/freelancer/FreelancerExpertiseSection'
import AddSkillLinkDialog from 'components/freelancer/AddSkillLinkDialog'
import firebase from 'utils/firebase'

const FreelancerManageRoles = ({ user, markets, onNext, stepIndex, totalSteps, onboarding }) => {
  const history = useHistory()
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [roleToLink, setRoleToLink] = useState(null)
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Add a new role - no existing role data needed
  const handleAddRole = () => {
    // Clear any previously stored role data
    sessionStorage.removeItem('editingRole');
    // Navigate to the FreelancerPrimaryRole component
    history.push('/roles/edit');
  }
  
  // Edit an existing role - pass the role data to the edit component
  const handleEditRole = (role) => {
    // Store the role data in sessionStorage to access it in the edit component
    sessionStorage.setItem('editingRole', JSON.stringify(role));
    // Navigate to the FreelancerPrimaryRole component with the role data
    history.push('/roles/edit');
  }

  const onAddSkillTag = (link) => {
    const { role, skill } = roleToLink
    const existingLinks = { ...(role.links || {}) }
    updateRoles(user, {
      ...role,
      links: { ...existingLinks, [skill]: link },
    })

    setRoleToLink(null)
  }

  const onRemoveSkillTag = () => {
    const { role, skill } = roleToLink
    const existingLinks = { ...(role.links || {}) }
    setRoleToLink(null)
    updateRoles(user, {
      ...role,
      links: {
        ...existingLinks,
        [skill]: firebase.firestore.FieldValue.delete(),
      },
    })
  }

  return (
    <Frame maxWidth="xs">
      <Dialog
        open={!!roleToDelete}
        onClose={() => setRoleToDelete(null)}
        title="Are you sure?"
        body="Are you sure you want to delete this role?"
        confirmText="Delete"
        onClick={() => {
          setRoleToDelete(null)
          deleteRole(user, roleToDelete)
        }}
      />

      <AddSkillLinkDialog
        open={!!roleToLink}
        onClose={() => setRoleToLink(null)}
        onRemove={onRemoveSkillTag}
        initialValue={
          roleToLink?.role.links
            ? roleToLink?.role.links[roleToLink?.skill]
            : null
        }
        skill={roleToLink?.skill || ''}
        onSubmit={onAddSkillTag}
      />

      <Heading>Manage Your Expertise</Heading>
      <Box mt={1} mb={4}>
        <Typography align="center">
          Edit, remove, or add roles to your profile
        </Typography>
      </Box>

      <Divider variant="middle" />

      {Object.values(user.roles || {}).length === 0 ? (
        <Box my={4} textAlign="center">
          <Typography variant="body1">
            You don't have any roles added yet. Click the button below to add your first role.
          </Typography>
        </Box>
      ) : (
        Object.values(user.roles || {})
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((role, index) => (
            <RoleItem
              user={user}
              role={role}
              deleteRole={() => setRoleToDelete(role)}
              onAddLink={(role, skill) => setRoleToLink({ role, skill })}
              key={role.id || role.roleId || `role-${role.industry || 'unknown'}-${role.label || 'unknown'}-${index}`}
              onEdit={() => handleEditRole(role)}
            />
          ))
      )}

      <Box mt={6} display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: '1rem' }}>
        <Button
          id="freelancer-add-role"
          variant="contained"
          style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
          onClick={handleAddRole}
        >
          {Object.values(user.roles || {}).length === 0 ? 'Add Your First Role' : 'Add Another Role'}
        </Button>
        
        <Button
          variant="contained"
          style={{ backgroundColor: buttonColor, color: 'white', minWidth: 180 }}
          onClick={() => history.push('/profile')}
        >
          Back to Profile
        </Button>
      </Box>
    </Frame>
  )
}

const RoleItem = ({ user, role, deleteRole, onAddLink, onEdit }) => {
  const [experience, setExperience] = useState(role.experience || 0)

  // Update role experience when slider changes and stops
  const handleExperienceChange = (newValue) => {
    setExperience(newValue)
    // Save the new experience value
    updateRoles(user, {
      ...role,
      experience: newValue
    })
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{role.label}</Typography>

        <CloseIcon
          onClick={deleteRole}
          width={30}
          style={{
            margin: '0 5px',
            fill: 'red',
            opacity: 0.5,
            cursor: 'pointer',
          }}
        />
      </Box>
      <Box my={1}>
        <ExperienceSlider
          value={experience}
          setValue={handleExperienceChange}
        />
      </Box>

      <Box display="flex" alignItems="center" flexWrap="wrap">
        {(role.skills || []).map((skill, index) => (
          <SkillPill
            key={`${skill}-${index}`}
            role={role}
            onClick={() => onAddLink(role, skill)}
            skill={skill}
          />
        ))}
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onEdit(role)}
          style={{ marginRight: 8, marginBottom: 8, padding: 14 }}
        >
          Edit
        </Button>
      </Box>

      <Divider my={3} />
    </>
  )
}

export default FreelancerManageRoles
